// NPM
import { computed, unref, type MaybeRef } from 'vue';

// Types
import type { UserDashboardRelationsGetHelperRelationDetailsResponseType } from '@magma-app/magma-types/all';

type RelationDetails =
  UserDashboardRelationsGetHelperRelationDetailsResponseType;

/** The server derives one state for both the list and detail, including exchange approval. */
export type HelperRelationState = NonNullable<RelationDetails['state']>;

export function useRelationState(
  relation: MaybeRef<RelationDetails | null | undefined>
) {
  const state = computed<HelperRelationState>(
    () => unref(relation)?.state ?? 'toContact'
  );
  /** The exchange is over: nothing the ambassador does can change it. */
  const isTerminal = computed(() =>
    (
      [
        'reported',
        'rejected',
        'accepted',
        'exchangeValidated',
        'closedUnreviewed'
      ] as HelperRelationState[]
    ).includes(state.value)
  );

  /** The ambassador still has something to do. Drives the primary action. */
  const needsAmbassador = computed(() =>
    (['toContact', 'contacted', 'toConfirm'] as HelperRelationState[]).includes(
      state.value
    )
  );

  /**
   * The prospect missed their own deadline, so the ambassador can ask the campaign team for help.
   * Backed by `helpeeExceedingDeadlineAt` — today this only opens from an email deep link.
   */
  const canContactAdmin = computed(
    () =>
      !!unref(relation)?.relation.relationMoment.helper?.canContactAdmin &&
      !isTerminal.value
  );

  /** i18n key for the primary button, or null when there is nothing to do. */
  const primaryActionKey = computed<string | null>(() => {
    switch (state.value) {
      case 'toContact':
      case 'contacted':
      case 'toConfirm':
        return 'helperRelation.action.confirm';
      default:
        return null;
    }
  });

  return {
    state,
    isTerminal,
    needsAmbassador,
    canContactAdmin,
    primaryActionKey
  };
}
